import { IconButton, ListItemButton, Menu, SvgIcon } from '@mui/material'
import { IconDotsVertical } from '@tabler/icons-react'
import * as R from 'remeda'
import { useState } from 'react'
import { SimpleDialog } from '~/ui/components/SimpleDialog'
import { useSendWaitlistNotification } from '~/features/customers/hooks/useSendWaitlistNotification'
import { useUpdateCustomerRequest } from '~/features/customers/hooks/useUpdateCustomerRequest'
import { getProductDetailPageTitle } from '~/features/routes/utils'
import { countryMap } from '~/features/visitors/constants'
import { getEshopLink } from '~/features/routes'
import { toast } from 'sonner'
import { ModelV2 } from '~/supabase/types'
import { useGetModelPhotos } from '~/features/models/models-list/hooks/useGetModelPhotos'
import { extractImageIndexFromPath } from '~/features/models/utils'

type Props = {
  customerName: string
  email: string
  country: string | null
  model: ModelV2 | undefined
  requestId: number
}

export const WaitlistActionMenu = ({ requestId, customerName, email, model, country }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const { mutate: sendNotification, isPending: isSending } = useSendWaitlistNotification()
  const { mutate: updateRequest, isPending: isUpdating } = useUpdateCustomerRequest()

  const { mutateAsync: getModelPhotos, isPending: isGettingModelPhotos } = useGetModelPhotos()

  const handleConfirm = async () => {
    if (!model) return
    const { material, product_type, id, supplier_code } = model
    const product_title = getProductDetailPageTitle({
      material,
      product_type,
      id,
      locale: country === countryMap.czechia ? 'cs' : 'en',
    })
    const product_link = getEshopLink.productDetail({ material, product_type, id })

    const images = await getModelPhotos({ supplierCode: supplier_code, material })

    if (!images || images.length === 0)
      return toast.error('Could not find an image of this product to send with email', {
        description: 'Please make sure the image file name is accurate in the storage',
      })
    const img_src = R.pipe(
      images,
      R.sortBy((image) => extractImageIndexFromPath(image) ?? 0),
      R.first()
    )

    if (!img_src)
      return toast.error('Could not find an image of this product to send with email', {
        description: 'Please make sure the image file name is accurate in the storage',
      })

    sendNotification(
      { to: email, img_src, stock_code: model.stock_code, product_link, product_title },
      {
        onSuccess: () =>
          updateRequest(
            { id: requestId, status: 'complete' },
            { onSuccess: () => setIsDialogOpen(false) }
          ),
      }
    )
  }

  const description = model
    ? [
        `Customer Name: ${customerName || 'Unknown'}`,
        `Email: ${email}`,
        `Product: ${model.stock_code}`,
      ].join('\n')
    : 'Without a model you can not send this email!'

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <SvgIcon fontSize="small">
          <IconDotsVertical />
        </SvgIcon>
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <ListItemButton
          onClick={() => {
            setAnchorEl(null)
            setIsDialogOpen(true)
          }}
        >
          Send notification
        </ListItemButton>
      </Menu>
      <SimpleDialog
        open={isDialogOpen}
        title="Send Waitlist Notification"
        text={model ? 'Send a back-in-stock email to this customer?' : 'Model seems to be missing!'}
        description={description}
        onSubmit={handleConfirm}
        onClose={() => setIsDialogOpen(false)}
        isLoading={isSending || isUpdating || isGettingModelPhotos}
        disabled={!model}
        confirmText="Send Email"
      />
    </>
  )
}
